import { makeObservable, observable, action } from 'mobx';
import { RootStore } from './Root.store';
import EventEmitter from 'events';

export enum pages {
    default = '',
    auth = 'auth',
    register = 'register',
    password_recovery = 'password_recovery',
    password_confirm = 'password_confirm',
    confirm = 'confirm',
    confirm_success = 'confirm_success',
    history = 'history',
    reports = 'reports',
    statistics = 'statistics',
    subusers = 'subusers',
}

export enum windows {
    map = 'map',
    deviceSettings = 'deviceSettings',
    deviceEdit = 'deviceEdit',
    deviceInfo = 'deviceInfo',
    addDevice = 'addDevice',
    addDeviceInDb = 'addDeviceInDb',
    addOldDevice = 'addOldDevice'
}

export type page = `${pages}`;
export type window = `${windows}`;

export default class AppStore {
    @observable
    public cookiesApproved: boolean;

    @observable
    public page: string;

    @observable
    public mainWindow: window;

    @observable
    public deviceSettingsVisible: boolean;

    @observable
    public deviceEditVisible: boolean;

    @observable
    public deviceInfoVisible: boolean;

    @observable
    public addDeviceVisible: boolean;

    @observable
    public addDeviceInDbVisible: boolean;

    @observable
    public isDeviceSettingsClosing: boolean;

    @observable
    public isDeviceSettingsClosed: boolean;

    @observable
    public search: string;

    @observable
    public demoMode: boolean;

    @observable
    public demoModeError: boolean;

    @observable
    public isOldLogin: boolean;

    @observable
    public isOpenModalAddOldDevice: boolean;

    @observable
    public pageList = Object.keys(pages);

    constructor(private rootStore: RootStore) {
        this.setDefault();
        makeObservable(this);
    }

    @action
    setDefault() {
        this.cookiesApproved =
            localStorage.getItem('cookiesApproved') === 'true';
        this.page = '';
        this.mainWindow = windows.map;
        this.deviceSettingsVisible = false;
        this.deviceEditVisible = false;
        this.deviceInfoVisible = false;
        this.addDeviceVisible = false;
        this.addDeviceInDbVisible = false;
        this.isDeviceSettingsClosing = false;
        this.isDeviceSettingsClosed = false;
        this.search = '';
        this.demoMode = localStorage.getItem('demoMode') === 'true';
        this.demoModeError = false;
        this.isOldLogin = false;
        this.isOpenModalAddOldDevice = false
    }

    @action
    setCookiesApproved() {
        this.cookiesApproved = true;
    }

    @action
    setPage(page: string) {
        this.page = page;
    }

    @action
    openDeviceSettings() {
        this.closeDeviceEdit();
        this.closeDeviceInfo();
        this.closeAddDevice();
        this.closeAddDeviceInDb();
        this.deviceSettingsVisible = true;
        this.mainWindow = windows.deviceSettings;
    }

    @action
    closeDeviceSettings() {
        if (
            this.deviceSettingsVisible &&
            this.mainWindow === windows.deviceSettings
        ) {
            this.deviceSettingsVisible = false;
            this.mainWindow = windows.map;
        }
    }

    @action
    openDeviceEdit() {
        this.closeDeviceSettings();
        this.closeDeviceInfo();
        this.closeAddDevice();
        this.closeAddDeviceInDb();
        this.deviceEditVisible = true;
        this.mainWindow = windows.deviceEdit;
    }

    @action
    closeDeviceEdit() {
        if (this.deviceEditVisible && this.mainWindow === windows.deviceEdit) {
            this.deviceEditVisible = false;
            this.mainWindow = windows.map;
            this.rootStore.devices.resetSelectedDeviceImei();
        }
    }

    @action
    openDeviceInfo() {
        this.closeDeviceSettings();
        this.closeDeviceEdit();
        this.closeAddDevice();
        this.closeAddDeviceInDb();
        this.deviceInfoVisible = true;
        this.mainWindow = windows.deviceInfo;
    }

    @action
    closeDeviceInfo() {
        if (this.deviceInfoVisible && this.mainWindow === windows.deviceInfo) {
            this.deviceInfoVisible = false;
            this.mainWindow = windows.map;
            this.rootStore.devices.resetInfoDeviceImei();
        }
    }

    @action
    openAddDevice() {
        this.closeDeviceSettings();
        this.closeDeviceEdit();
        this.closeDeviceInfo();
        this.closeAddDeviceInDb();
        this.addDeviceVisible = true;
        this.mainWindow = windows.addDevice;
    }

    @action
    closeAddDevice() {
        if (this.addDeviceVisible && this.mainWindow === windows.addDevice) {
            this.addDeviceVisible = false;
            this.mainWindow = windows.map;
        }
    }

    @action
    openAddDeviceInDb() {
        this.closeDeviceSettings();
        this.closeDeviceEdit();
        this.closeDeviceInfo();
        this.closeAddDevice();
        this.addDeviceInDbVisible = true;
        this.mainWindow = windows.addDeviceInDb;
    }

    @action
    closeAddDeviceInDb() {
        if (
            this.addDeviceInDbVisible &&
            this.mainWindow === windows.addDeviceInDb
        ) {
            this.addDeviceInDbVisible = true;
            this.mainWindow = windows.map;
        }
    }

    @action
    closeAllWindows() {
        this.closeDeviceSettings();
        this.closeDeviceEdit();
        this.closeDeviceInfo();
    }

    @action
    setSearch(val: string) {
        this.search = val;
    }

    @action
    setDeviceSettingsClosing() {
        this.isDeviceSettingsClosing = true;
    }

    @action
    resetDeviceSettingsClosing() {
        this.isDeviceSettingsClosing = false;
    }

    @action
    setDeviceSettingsClosed() {
        this.isDeviceSettingsClosed = true;
    }

    @action
    resetDeviceSettingsClosed() {
        this.isDeviceSettingsClosed = false;
    }

    @action
    setDemoMode() {
        this.demoMode = true;
    }

    @action
    resetDemoMode() {
        this.demoMode = false;
    }

    @action
    setDemoModeError() {
        this.demoModeError = true;
    }

    @action
    resetDemoModeError() {
        this.demoModeError = false;
    }
}
